/**
 * Cart drawer / cart page "Complete your set" quick-add suggestions.
 *
 * Fetches Shopify product recommendations for the first item in the cart
 * (complementary, falling back to related, falling back to a best-sellers
 * collection), renders 2-3 cards, and wires a quick-add button that posts
 * to /cart/add.js and re-renders the cart drawer / cart page sections that
 * already power the rest of the cart's AJAX updates. No page reload.
 */
(function () {
  var CARD_LIMIT = 3;

  function moneyFormat(amount, currencyCode) {
    var value = Number(amount);
    if (isNaN(value)) return '';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode || 'USD',
      }).format(value);
    } catch (e) {
      return '$' + value.toFixed(2);
    }
  }

  function excludedIds(root) {
    var raw = root.getAttribute('data-cart-item-ids') || '';
    return raw
      .split(',')
      .map(function (v) { return v.trim(); })
      .filter(Boolean);
  }

  function fetchJSON(url) {
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (res) {
      if (!res.ok) throw new Error('Request failed: ' + url);
      return res.json();
    });
  }

  function getRecommendations(productId) {
    var complementaryUrl =
      '/recommendations/products.json?product_id=' +
      encodeURIComponent(productId) +
      '&intent=complementary&limit=6';
    var relatedUrl =
      '/recommendations/products.json?product_id=' +
      encodeURIComponent(productId) +
      '&intent=related&limit=6';

    return fetchJSON(complementaryUrl)
      .then(function (data) {
        if (data && data.products && data.products.length) return data.products;
        return fetchJSON(relatedUrl).then(function (data2) {
          return (data2 && data2.products) || [];
        });
      })
      .catch(function () {
        return fetchJSON(relatedUrl)
          .then(function (data2) {
            return (data2 && data2.products) || [];
          })
          .catch(function () {
            return [];
          });
      });
  }

  function getFallbackProducts(collectionUrl) {
    if (!collectionUrl) return Promise.resolve([]);
    var url = collectionUrl.split('?')[0].replace(/\/$/, '') + '/products.json?limit=6';
    return fetchJSON(url)
      .then(function (data) {
        return (data && data.products) || [];
      })
      .catch(function () {
        return [];
      });
  }

  function pickVariant(product) {
    if (!product.variants || !product.variants.length) return null;
    var available = product.variants.filter(function (v) {
      return v.available !== false;
    });
    return available[0] || product.variants[0];
  }

  function renderCard(product, currencyCode) {
    var variant = pickVariant(product);
    if (!variant) return null;

    var image =
      (product.featured_image && (product.featured_image.src || product.featured_image)) ||
      (product.images && product.images[0] && (product.images[0].src || product.images[0])) ||
      '';
    if (image && image.indexOf('//') === 0) image = 'https:' + image;

    var card = document.createElement('div');
    card.className = 'cart-suggestions__card';

    var media = document.createElement('a');
    media.className = 'cart-suggestions__card-media';
    media.href = product.url || '#';
    media.tabIndex = -1;
    media.setAttribute('aria-hidden', 'true');
    if (image) {
      var img = document.createElement('img');
      img.src = image;
      img.alt = '';
      img.loading = 'lazy';
      img.width = 80;
      img.height = 80;
      media.appendChild(img);
    }

    var body = document.createElement('div');
    body.className = 'cart-suggestions__card-body';

    var title = document.createElement('a');
    title.className = 'cart-suggestions__card-title';
    title.href = product.url || '#';
    title.textContent = product.title;

    var price = document.createElement('span');
    price.className = 'cart-suggestions__card-price';
    // variant.price from the recommendations/products JSON endpoints comes
    // back in cents (e.g. 3000 for $30.00), same as Shopify's cart AJAX API.
    // moneyFormat() expects a plain currency amount (30, not 3000), so this
    // was rendering $3,000.00 for a $30.00 mat -- divide by 100 first.
    price.textContent = moneyFormat(variant.price / 100, currencyCode);

    body.appendChild(title);
    body.appendChild(price);

    var addButton = document.createElement('button');
    addButton.type = 'button';
    addButton.className = 'cart-suggestions__add';
    addButton.setAttribute('data-variant-id', variant.id);
    addButton.textContent = '+ Add';

    card.appendChild(media);
    card.appendChild(body);
    card.appendChild(addButton);

    return card;
  }

  function renderCards(root, products) {
    var grid = root.querySelector('.cart-suggestions__grid');
    if (!grid) return;
    grid.innerHTML = '';

    if (!products.length) {
      root.hidden = true;
      return;
    }

    var currencyCode = root.getAttribute('data-currency');
    products.slice(0, CARD_LIMIT).forEach(function (product) {
      var card = renderCard(product, currencyCode);
      if (card) grid.appendChild(card);
    });

    root.hidden = grid.children.length === 0;
  }

  function loadSuggestions(root) {
    root.setAttribute('data-initialized', 'true');
    var productId = root.getAttribute('data-product-id');
    var exclude = excludedIds(root);
    var fallbackCollection = root.getAttribute('data-fallback-collection-url');

    if (!productId) {
      root.hidden = true;
      return;
    }

    getRecommendations(productId)
      .then(function (products) {
        var filtered = products.filter(function (p) {
          return exclude.indexOf(String(p.id)) === -1;
        });
        if (filtered.length) return filtered;
        return getFallbackProducts(fallbackCollection).then(function (fallbackProducts) {
          return fallbackProducts.filter(function (p) {
            return exclude.indexOf(String(p.id)) === -1;
          });
        });
      })
      .then(function (products) {
        renderCards(root, products);
      })
      .catch(function () {
        root.hidden = true;
      });
  }

  function scan() {
    document
      .querySelectorAll('cart-suggestions[data-product-id]:not([data-initialized])')
      .forEach(loadSuggestions);
  }

  function handleQuickAdd(event) {
    var button = event.target.closest('.cart-suggestions__add');
    if (!button) return;
    event.preventDefault();
    if (button.disabled) return;

    var variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    var originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: 'cart-drawer,cart-icon-bubble,cart-items',
        sections_url: window.location.pathname,
      }),
    })
      .then(function (res) {
        return res.json().then(function (data) {
          return { ok: res.ok, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          throw new Error((result.data && result.data.description) || 'Could not add to cart');
        }
        var data = result.data;

        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.renderContents(data);
          if (typeof drawer.open === 'function') drawer.open();
        }

        if (data.sections && data.sections['cart-items']) {
          var currentItems = document.querySelector('cart-items');
          if (currentItems) {
            var doc = new DOMParser().parseFromString(data.sections['cart-items'], 'text/html');
            var newItems = doc.querySelector('cart-items');
            if (newItems) currentItems.replaceWith(newItems);
          }
        }

        button.textContent = 'Added';
        setTimeout(scan, 50);
      })
      .catch(function () {
        button.disabled = false;
        button.textContent = originalText;
      });
  }

  document.addEventListener('click', handleQuickAdd);

  function observe() {
    var observer = new MutationObserver(function () {
      scan();
    });
    ['#CartDrawer', '#main-cart-items', 'cart-items'].forEach(function (selector) {
      var el = document.querySelector(selector);
      if (el) observer.observe(el, { childList: true, subtree: true });
    });
  }

  function init() {
    scan();
    observe();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
